import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { tap } from 'rxjs';

const BODY_PREVIEW_LENGTH = 100;
const TOKEN_KEY = 'qrRouterToken';
const STALE_AUTH_ERROR_KEY = 'wahlStaleAuthRequest';

export const httpDiagnosticsInterceptor: HttpInterceptorFn = (request, next) => {
  const startedAt = performance.now();
  const requestUrl = request.urlWithParams;
  const requestAuthorization = request.headers.get('Authorization') || '';

  return next(request).pipe(
    tap({
      error: (error) => {
        if (!(error instanceof HttpErrorResponse)) {
          console.warn('[Wahl API] error', {
            method: request.method,
            url: requestUrl,
            error
          });
          return;
        }

        markStaleAuthRequest(error, requestAuthorization);

        const details = {
          method: request.method,
          url: requestUrl,
          status: error.status,
          contentType: error.headers.get('Content-Type') || null,
          durationMs: elapsedMs(startedAt)
        };

        if (isJsonParseError(error)) {
          console.error('[Wahl API] parse error', {
            ...details,
            bodyStart: bodyPreview(error)
          });
          return;
        }

        console.warn('[Wahl API] error', details);
      }
    })
  );
};

export function isStaleAuthRequestError(error: unknown): boolean {
  return Boolean(
    error &&
      typeof error === 'object' &&
      STALE_AUTH_ERROR_KEY in error &&
      (error as Record<typeof STALE_AUTH_ERROR_KEY, unknown>)[STALE_AUTH_ERROR_KEY]
  );
}

function markStaleAuthRequest(error: HttpErrorResponse, requestAuthorization: string): void {
  if (!requestAuthorization.startsWith('Bearer ')) {
    return;
  }

  const currentToken = localStorage.getItem(TOKEN_KEY) || '';
  const currentAuthorization = currentToken ? `Bearer ${currentToken}` : '';

  if (requestAuthorization !== currentAuthorization) {
    Object.defineProperty(error, STALE_AUTH_ERROR_KEY, {
      value: true,
      configurable: true
    });
  }
}

function elapsedMs(startedAt: number): number {
  return Math.round(performance.now() - startedAt);
}

function isJsonParseError(error: HttpErrorResponse): boolean {
  const responseError = error.error;

  if (responseError instanceof SyntaxError) {
    return true;
  }

  if (!responseError || typeof responseError !== 'object') {
    return false;
  }

  const nestedError = 'error' in responseError ? responseError.error : null;
  return nestedError instanceof SyntaxError;
}

function bodyPreview(error: HttpErrorResponse): string {
  const responseError = error.error;
  let body = '';

  if (typeof responseError === 'string') {
    body = responseError;
  } else if (responseError && typeof responseError === 'object' && 'text' in responseError && typeof responseError.text === 'string') {
    body = responseError.text;
  }

  return redactSensitiveText(body).slice(0, BODY_PREVIEW_LENGTH);
}

function redactSensitiveText(value: string): string {
  return value
    .replace(/Bearer\s+[-._~+/=A-Za-z0-9]+/g, 'Bearer <redacted>')
    .replace(/eyJ[-._~+/=A-Za-z0-9]+/g, '<jwt-redacted>');
}
